import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    Button, Col, FormGroup, Input, Label,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { MetaTags } from "react-meta-tags";
import { useHistory } from "react-router-dom";
import * as pageId from "../../../routes/allPageID";
import * as mode from "../../../routes/PageMode";
import { PageLoadingSpinner, SaveButton } from "../../../components/Common/CommonButton";
import SaveButtonDraggable from "../../../components/Common/saveButtonDraggable";
import { C_Select } from "../../../CustomValidateForm";
import GlobalCustomTable from "../../../GlobalCustomTable";
// adjust the path to the file that exports deltBtnCss (the list action buttons file)
import { deltBtnCss } from "../../../components/Common/ListActionsButtons";
import { alertMessages } from "../../../components/Common/CommonErrorMsg/alertMsg";
import {
    amountCommaSeparateFunc,
    breadcrumbReturnFunc,
    date_ymd_func,
    loginSelectedPartyID,
    metaTagLabel,
} from "../../../components/Common/CommonFunction";
import { comAddPageFieldFunc, initialFiledFunc } from "../../../components/Common/validationFunction";
import { BreadcrumbShowCountlabel, commonPageField, commonPageFieldSuccess } from "../../../store/actions";
import {
    getCashBookPartyHeads,
    saveCashBookPartyHead,
    saveCashBookPartyHead_Success,
    updateCashBookEntryID,
    updateCashBookEntryIDSuccess,
} from "../../../store/Administrator/CashBookEntryRedux/action";
import { CashBookEntryPostAPI, CashierName_Api, PaymentModeDropdownAPIGET } from "../../../helpers/backend_helper";
import { customAlert } from "../../../CustomAlert/ConfirmDialog";
// shared password popup (same one the list page uses)
import CashierVerifyModal from "./Cashierverifymodal";
import { url } from "../../../routes";

// Confirm dialog type used by the list page delete button.
const CONFIRM_ALERT_TYPE = 8;

// accept both spellings coming from the API
const flagOf = (obj, name) => {
    if (!obj) return false;
    const alt = name === "IsOpening" ? "IsOpeaning" : name;
    return !!(obj[name] ?? obj[alt]);
};

// "2026-10-22T14:35:00" or "2026-10-22 14:35:00" -> "22-10-2026 14:35"
const formatDateTime = (v) => {
    if (!v) return "-";
    const d = new Date(String(v).replace(" ", "T"));
    if (isNaN(d.getTime())) return String(v);      // unknown format: show as received
    const p = (n) => String(n).padStart(2, "0");
    const h24 = d.getHours();
    const h12 = h24 % 12 || 12;
    const ampm = h24 >= 12 ? "PM" : "AM";
    return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} (${p(h12)}:${p(d.getMinutes())}:${p(d.getSeconds())} ${ampm})`;
};

// Expense / Minus / "-" -> -1, Income / Plus / "+" -> +1. Missing flag counts as +1 (Opening etc.)
const signOf = (expInc) => {
    const v = String(expInc ?? "").trim().toLowerCase();
    if (v === "expense" || v === "minus" || v === "-") return -1;
    return 1;
};

// sum of every row except Closing, each row added or subtracted by its flag
const closingOf = (rows) =>
    Number(
        rows
            .filter((r) => !r.IsClosing)
            .reduce((sum, r) => sum + signOf(r.ExpInc) * (Number(r.Amount) || 0), 0)
            .toFixed(2)
    );

// Opening first, Closing last, everything else stays in the middle (original order kept).
const sortRows = (rows) => {
    const rank = (r) => (r.IsOpening ? 0 : r.IsClosing ? 2 : 1);
    return rows
        .map((r, i) => ({ r, i }))
        .sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i)
        .map((x) => x.r);
};

// the single API already returns head + saved record combined
const mapApiRows = (data) =>
    sortRows(
        (data || [])
            .filter((h) => h.IsActive !== false)
            .map((h) => ({
                key: `head-${h.PartyHead_id}-${h.CashBook_id ?? "new"}`,
                CashBookID: h.CashBook_id ?? null,
                PartyID: loginSelectedPartyID(),
                ParticularHeadID: h.PartyHead_id,
                ExpInc: h.ExpInc,
                HeadName: h.HeadName || "",
                PaymentModeID: h.PaymentModeID,
                // not saved yet -> show empty input instead of 0
                Amount: h.CashBook_id ? h.Amount : "",
                Comment: h.Comment || "",
                IsOpening: flagOf(h, "IsOpening"),
                IsClosing: flagOf(h, "IsClosing"),
                CreatedOn: h.Created_on ?? h.CreatedOn ?? null,
                IsReserved: !!h.IsReserved,
                // Opening row that arrived with 0 amount stays editable even though it is reserved
                OpeningEditable: flagOf(h, "IsOpening") && !!h.IsReserved && Number(h.Amount || 0) === 0,
            }))
    );

const CashBookEntry = (props) => {
    const editBoundRef = useRef(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const location = { ...history.location };
    const isModal = props.hasOwnProperty(mode.editValue);
    const originalRef = useRef({});
    const pendingRef = useRef(null);
    const rowsRef = useRef([]);
    // keys must match the ControlID / field names in your PageMaster entry
    const fileds = {
        Head: "",
        Party: "",
        Cashier: "",
        Date: "",
        Amount: "",
        Comment: "",
    };

    const [state, setState] = useState(() => initialFiledFunc(fileds));
    const [userPageAccessState, setUserPageAccessState] = useState('');
    const [entryDate, setEntryDate] = useState(() => date_ymd_func());   // Y-m-d
    const [cashier, setCashier] = useState(null);
    const [rows, setRows] = useState([]);
    rowsRef.current = rows;
    const [modeOptions, setModeOptions] = useState([]);
    const [cashierOptions, setCashierOptions] = useState([]);
    // true after successful verification: the list is generated and Date / Cashier are locked until Change is clicked.
    const [listGenerated, setListGenerated] = useState(false);
    // verify popup open / close (all password logic lives inside CashierVerifyModal)
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const {
        userAccess, pageField, partyHeadList, headListLoading, postMsg, saveBtnloading, updateMsg
    } = useSelector((state) => ({
        userAccess: state.Login.RoleAccessUpdateData,
        pageField: state.CommonPageFieldReducer.pageField,
        partyHeadList: state.CashBookEntryReducer.partyHeadList,
        headListLoading: state.CashBookEntryReducer.headListLoading,
        postMsg: state.CashBookEntryReducer.postMsg,
        saveBtnloading: state.CashBookEntryReducer.saveBtnloading,
        updateMsg: state.CashBookEntryReducer.updateMessage,
    }));

    // page fields + party wise heads
    useEffect(() => {
        dispatch(commonPageFieldSuccess(null));
        dispatch(commonPageField(pageId.CASH_BOOK_ENTRY));
    }, []);

    // edit mode: ask the API with the saved date + cashier
    useEffect(() => {
        const records = props.editValue?.Records ?? location.editValue?.Records;
        if (!records?.length || !cashierOptions.length) return;
        if (editBoundRef.current) return;
        editBoundRef.current = true;

        const first = records[0];
        pendingRef.current = {
            type: "edit",
            date: first.Date,
            cashier: cashierOptions.find((o) => String(o.value) === String(first.CashierID)) || null,
        };
        dispatch(getCashBookPartyHeads({
            PartyID: loginSelectedPartyID(),
            CashierID: first.CashierID,
            Date: first.Date,
        }));
    }, [cashierOptions]);

    // API response arrived (Add + Edit): build the rows
    useEffect(() => {
        if (headListLoading || !pendingRef.current) return;
        const pending = pendingRef.current;
        pendingRef.current = null;

        const merged = mapApiRows(partyHeadList);

        originalRef.current = {};
        merged.forEach((r) => {
            if (r.CashBookID) originalRef.current[r.CashBookID] = { Amount: r.Amount, Comment: r.Comment };
        });

        if (merged.length === 0) {
            customAlert({ Type: 3, Message: "No Heads available" });
            return;
        }
        if (pending.type === "edit") {
            setEntryDate(pending.date);
            setCashier(pending.cashier);
            setIsEdit(true);
        }
        setRows(merged);
        setListGenerated(true);
    }, [headListLoading]);

    useEffect(() => {
        if (isModal || updateMsg?.Status !== true) return;

        // clear the stored response first so it can't fire twice
        dispatch(updateCashBookEntryIDSuccess({ Status: false }));

        if (updateMsg.StatusCode === 200) {
            (async () => {
                await customAlert({ Type: 1, Message: updateMsg.Message });
                history.push({ pathname: url.CASH_BOOK_ENTRY_LIST, state: { fromSave: true } });
            })();
        } else {
            customAlert({ Type: 4, Message: JSON.stringify(updateMsg.Message) });
        }
    }, [updateMsg]);
    // Breadcrumb: row count + total amount (same format as the list pages)
    useEffect(() => {
        if (userPageAccessState === '' || isModal) return;

        const totalAmount = closingOf(rows);

        dispatch(
            BreadcrumbShowCountlabel(
                `Count:${rows.length} currency_symbol ${amountCommaSeparateFunc(totalAmount.toFixed(2))}`
            )
        );
    }, [rows, userPageAccessState]);

    // payment modes + cashiers
    useEffect(() => {
        const partyId = loginSelectedPartyID();

        const fetchModes = async () => {
            try {
                const resp = await PaymentModeDropdownAPIGET(partyId);
                if (resp?.StatusCode === 200) {
                    setModeOptions(
                        (resp.Data || []).map((item) => ({
                            value: item.id,
                            label: item.Payment_Mode,
                            TenderCode: item.TenderCode,
                            Description: item.Description,
                        }))
                    );
                } else if (resp?.Message) {
                    customAlert({ Type: 4, Message: resp.Message });
                }
            } catch (error) {
                setModeOptions([]);
                customAlert({ Type: 4, Message: "Unable to load Payment Modes" });
            }
        };

        const fetchCashiers = async () => {
            try {
                const resp = await CashierName_Api({
                    jsonBody: JSON.stringify({ Party: partyId }),
                });
                if (resp?.StatusCode === 200) {
                    setCashierOptions(resp.Data || []); // already { value, label }
                } else if (resp?.Message) {
                    customAlert({ Type: 4, Message: resp.Message });
                }
            } catch (error) {
                setCashierOptions([]);
                customAlert({ Type: 4, Message: "Unable to load Cashiers" });
            }
        };

        fetchModes();
        fetchCashiers();
    }, []);

    // user access + breadcrumb (page name)
    useEffect(() => {
        const userAcc = userAccess.find((inx) => `/${inx.ActualPagePath}` === location.pathname);
        if (userAcc) {
            setUserPageAccessState(userAcc);
            breadcrumbReturnFunc({ dispatch, userAcc });
        }
    }, [userAccess]);

    // apply PageMaster labels
    useEffect(() => {
        if (pageField) {
            const fieldArr = pageField.PageFieldMaster;
            comAddPageFieldFunc({ state, setState, fieldArr });
        }
    }, [pageField]);

    useEffect(() => {
        if (isModal || postMsg?.Status !== true) return;

        // clear the stored response first so it can't fire twice
        dispatch(saveCashBookPartyHead_Success({ Status: false }));

        if (postMsg.StatusCode === 200 || postMsg.StatusCode === 201) {
            setRows([]);
            setListGenerated(false);

            (async () => {
                await customAlert({ Type: 1, Message: postMsg.Message });
                history.push({
                    pathname: url.CASH_BOOK_ENTRY_LIST,
                    state: { fromSave: true },
                });
            })();
        } else {
            customAlert({ Type: 4, Message: JSON.stringify(postMsg.Message) });
        }
    }, [postMsg]);

    const { fieldLabel } = state;

    // Add: only validates the selection and opens the password popup.
    const addButtonHandler = () => {
        if (!cashier?.value) {
            customAlert({ Type: 3, Message: "Please select Cashier" });
            return;
        }
        setVerifyOpen(true);
    };

    const verifiedHandler = () => {
        setVerifyOpen(false);
        pendingRef.current = { type: "add" };
        dispatch(getCashBookPartyHeads({
            PartyID: loginSelectedPartyID(),
            CashierID: cashier.value,
            Date: entryDate,
        }));
    };
    // Change: remove the table and make Date / Cashier editable again.
    const changeButtonHandler = () => {
        setRows([]);
        setListGenerated(false);
    };

    const rowChangeHandler = useCallback((key, field, value) => {
        const next = rowsRef.current.map((r) => (r.key === key ? { ...r, [field]: value } : r));

        // Amount that would push the Closing total below zero is rejected
        if (field === "Amount" && closingOf(next) < 0) {
            customAlert({ Type: 3, Message: "Closing total cannot be negative" });
            return;
        }
        rowsRef.current = next;     // keeps fast typing in sync before the re-render
        setRows(next);
    }, []);

    // Closing row amount is always the sum of every other row (read-only).
    const displayRows = useMemo(() => {
        const rounded = closingOf(rows);
        return rows.map((r) => (r.IsClosing ? { ...r, Amount: rounded } : r));
    }, [rows]);

    // Same confirm dialog as the list page delete button.
    const deleteRowHandler = useCallback(async (row) => {
        const isPermission = await customAlert({
            Type: CONFIRM_ALERT_TYPE,
            Message: `${alertMessages.deleteOrNot} Entry : "${row.HeadName}"`,
        });
        if (isPermission) {
            setRows((prev) => prev.filter((r) => r.key !== row.key));
        }
    }, []);

    const closingStyle = (row) =>
        row.IsClosing
            ? { backgroundColor: "#e8f4ff", fontWeight: 700, borderTop: "2px solid #0d6efd" }
            : row.IsOpening
                ? { backgroundColor: "#f4fbf4" }
                : row.IsReserved
                    ? { backgroundColor: "#f5f5f5" }      // 👈 add
                    : {};

    // ---------------------- GlobalCustomTable columns ----------------------
    // isDummyField makes sure the input / select cells always re-render with the latest row data.
    const entryColumns = useMemo(() => [
        {
            dataField: "cashierCol",
            isDummyField: true,
            text: fieldLabel?.Cashier,
            headerStyle: () => ({ width: "16%" }),
            formatter: () => <span>{cashier?.label || "-"}</span>,
        },
        {
            dataField: "dateTimeCol",
            isDummyField: true,
            text: "Date & Time",
            headerStyle: () => ({ width: "14%", minWidth: "130px" }),
            style: (cell, row) => closingStyle(row),
            formatter: (cell, row) => <span>{formatDateTime(row.CreatedOn)}</span>,
        },
        {
            dataField: "HeadName",
            text: fieldLabel?.Head,
            headerStyle: () => ({ width: "20%" }),
            style: (cell, row) => closingStyle(row),
            formatter: (cell, row) => (
                <span>
                    {cell}{" "}
                    {row.IsOpening && <span className="badge bg-success ms-1">Opening</span>}
                    {row.IsClosing && <span className="badge bg-primary ms-1">Closing (Total)</span>}
                </span>
            ),
        },
        {
            dataField: "amountCol",
            isDummyField: true,
            text: fieldLabel?.Amount,
            headerStyle: () => ({ width: "14%", minWidth: "110px" }),
            style: (cell, row) => closingStyle(row),
            formatter: (cell, row) => {
                const locked = row.IsClosing || (row.IsReserved && !row.OpeningEditable);
                return (
                    <Input
                        type="number"
                        value={row.Amount}
                        disabled={locked}
                        style={
                            row.IsClosing
                                ? { fontWeight: 700, backgroundColor: "#dbeeff", cursor: "not-allowed" }
                                : locked
                                    ? { cursor: "not-allowed" }
                                    : undefined
                        }
                        onChange={(e) => !locked && rowChangeHandler(row.key, "Amount", e.target.value)}
                        placeholder="Please Enter Amount"
                    />
                );
            },
        },
        {
            dataField: "commentCol",
            isDummyField: true,
            text: fieldLabel?.Comment,
            headerStyle: () => ({ width: "26%", minWidth: "160px" }),
            style: (cell, row) => closingStyle(row),
            // commentCol formatter
            formatter: (cell, row) => {
                const locked = row.IsClosing || (row.IsReserved && !row.OpeningEditable);
                return (
                    <Input
                        value={row.Comment}
                        disabled={locked}
                        style={
                            row.IsClosing
                                ? { backgroundColor: "#dbeeff", cursor: "not-allowed" }
                                : locked
                                    ? { cursor: "not-allowed" }
                                    : undefined
                        }
                        onChange={(e) => !locked && rowChangeHandler(row.key, "Comment", e.target.value)}
                        placeholder="Please Enter Comment"
                    />
                );
            },
        },
    ], [fieldLabel, cashier, isEdit, rowChangeHandler, deleteRowHandler]);

    // ---- CashBook Entry save ----
    const SaveHandler = (event) => {
        event.preventDefault();
        try {
            if (!entryDate) {
                customAlert({ Type: 3, Message: "Please select Date" });
                return;
            }
            if (!cashier?.value) {
                customAlert({ Type: 3, Message: "Please select Cashier" });
                return;
            }
            if (rows.length === 0) {
                customAlert({ Type: 3, Message: "Please add at least one entry" });
                return;
            }

            // collect every problem, row by row, and show them together
            const msgs = [];
            rows.forEach((r, i) => {
                if (!r.IsClosing && Number(r.Amount) < 0) {
                    msgs.push({ [`Row ${i + 1} (${r.HeadName})`]: "Amount cannot be negative" });
                }
            });
            if (msgs.length > 0) {
                customAlert({ Type: 4, Message: JSON.stringify(msgs) });
                return;
            }

            const closingTotal = closingOf(displayRows);

            if (closingTotal < 0) {
                customAlert({ Type: 3, Message: "Closing total cannot be negative" });
                return;
            }

            // a row with no amount typed is not sent (Closing is always sent, it carries the total)
            const hasAmount = (r) => r.Amount !== "" && r.Amount !== null && r.Amount !== undefined;

            const payload = displayRows
                .filter((r) => r.IsClosing || r.OpeningEditable || hasAmount(r))
                .flatMap((r) => {
                    const amount = r.IsClosing
                        ? closingTotal
                        : hasAmount(r) ? Number(r.Amount) : 0;

                    const base = {
                        PartyID: Number(r.PartyID),
                        CashierID: cashier.value,
                        Date: entryDate,
                        ParticularHeadID: r.ParticularHeadID,
                        PaymentModeID: r.PaymentModeID,
                        IsOpening: !!r.IsOpening,
                        IsClosing: !!r.IsClosing,
                    };

                    // saved amount this row was loaded with
                    const orig = r.CashBookID ? originalRef.current[r.CashBookID] : null;
                    const origAmount = Number(orig?.Amount || 0);

                    // saved amount was increased: old row as it was + a new row for the difference
                    if (!r.IsClosing && !r.IsOpening && r.CashBookID && origAmount > 0 && amount > origAmount) {
                        return [
                            { ...base, ID: r.CashBookID, Amount: origAmount, Comment: orig.Comment || "" },
                            { ...base, ID: null, Amount: Number((amount - origAmount).toFixed(2)), Comment: r.Comment || "" },
                        ];
                    }

                    return [{ ...base, ID: r.CashBookID ?? null, Amount: amount, Comment: r.Comment || "" }];
                });

            if (payload.every((r) => r.IsClosing || r.IsOpening)) {
                customAlert({ Type: 3, Message: "Please enter amount for at least one entry" });
                return;
            }

            if (isEdit) {
                dispatch(updateCashBookEntryID({ jsonBody: JSON.stringify(payload) }));
                return;
            }
            dispatch(saveCashBookPartyHead({ jsonBody: JSON.stringify(payload) }));
        } catch (e) {
            customAlert({ Type: 4, Message: "Unable to save. Please check the entries and try again." });
        }
    };

    if (userPageAccessState === '') {
        return <React.Fragment></React.Fragment>;
    }

    return (
        <React.Fragment>
            <PageLoadingSpinner isLoading={headListLoading} />
            <MetaTags>{metaTagLabel(userPageAccessState)}</MetaTags>
            <div className="page-content">
                <div className="px-2 c_card_filter text-black">
                    <div className="row">
                        <Col sm="4">
                            <FormGroup className="mb-3 row mt-3 align-items-center">
                                <Label className="mt-2" style={{ width: "70px" }}>{fieldLabel?.Date}</Label>
                                <Col sm={8}>
                                    <div style={listGenerated ? { pointerEvents: "none", opacity: 0.65 } : undefined}>
                                        <Flatpickr
                                            id="entryDate"
                                            name="Date"
                                            className="form-control d-block p-2 bg-white text-dark"
                                            value={entryDate}
                                            options={{
                                                altInput: true,
                                                altFormat: "d-m-Y",
                                                dateFormat: "Y-m-d",
                                                clickOpens: !listGenerated,
                                            }}
                                            onChange={(selectedDates, dateStr) => setEntryDate(dateStr)}
                                        />
                                    </div>
                                </Col>
                            </FormGroup>
                        </Col>

                        <Col sm="4">
                            <FormGroup className="mb-3 row mt-3 align-items-center">
                                <Label className="mt-2" style={{ width: "70px" }}>{fieldLabel?.Cashier}</Label>
                                <Col sm={8}>
                                    <C_Select
                                        name="Cashier"
                                        value={cashier}
                                        isSearchable={true}
                                        isClearable={true}
                                        isDisabled={listGenerated}
                                        className="react-dropdown"
                                        classNamePrefix="dropdown"
                                        styles={{ menu: provided => ({ ...provided, zIndex: 2 }) }}
                                        options={cashierOptions}
                                        onChange={(e) => setCashier(e)}
                                    />
                                </Col>
                            </FormGroup>
                        </Col>

                        <Col sm="4" className="mt-3 text-end pe-5">
                            {listGenerated ? (
                                <div className="d-flex justify-content-end align-items-center gap-3">
                                    {!isEdit && (
                                        <Button color="info" onClick={changeButtonHandler}>
                                            Change
                                        </Button>
                                    )}
                                </div>
                            ) : (
                                <Button color="primary" onClick={addButtonHandler} disabled={!cashier}>
                                    Add
                                </Button>
                            )}
                        </Col>
                    </div>
                </div>

                {displayRows.length > 0 && (
                    <div className="mt-3 table-responsive">
                        <GlobalCustomTable
                            keyField="key"
                            data={displayRows}
                            columns={entryColumns}
                            classes="custom-table"
                            bordered
                            condensed
                            isPaginationTotalStandalone={false}
                            noDataIndication={
                                <div className="text-danger text-center">
                                    Record Not available
                                </div>
                            }
                        />
                    </div>
                )}

                {rows.length > 0 && (() => {
                    const saveBtn = (
                        <SaveButton
                            pageMode={isEdit ? mode.edit : mode.defaultsave}
                            loading={saveBtnloading}
                            onClick={SaveHandler}
                            userAcc={userPageAccessState}
                        />
                    );
                    return isModal
                        ? <div className="mt-3 text-start">{saveBtn}</div>
                        : <SaveButtonDraggable>{saveBtn}</SaveButtonDraggable>;
                })()}
            </div>

            {/* shared cashier password popup */}
            <CashierVerifyModal
                isOpen={verifyOpen}
                toggle={() => setVerifyOpen(false)}
                cashier={cashier}
                onVerified={verifiedHandler}
            />
        </React.Fragment>
    );
};

export default CashBookEntry;